// src/services/cloudVoice.js
//
// Makes EVERY game's read-aloud use the realistic cloud voice
// (functions/voice.js → Gemini text-to-speech), without changing the games.
//
// The games all talk through the browser's window.speechSynthesis
// (speak / cancel, with onstart / onboundary / onend on the utterance).
// installCloudVoice() swaps in a stand-in with the same shape:
//
//   speak(utterance)  → asks speakText for that sentence's .wav (saved on the
//                       server, so it's instant after the first time), plays
//                       it, and fires the same start / word / end events
//   cancel()          → stops it
//
// If the cloud voice can't be reached (not deployed, offline, daily limit)
// that sentence falls back to the browser voice — nothing breaks.
// Call once, at app start (src/index.js).
//
// SLOW IS NOT A FAILURE (Nov 2026). A sentence nobody has heard yet has to
// be made first (a few seconds, more if the function was asleep). It used to
// give up after 7 s and read it in the robotic device voice, while the
// natural one finished in the background — so the first press was slow AND
// robotic, and the second press was natural and instant. Now it waits for
// the natural voice (now up to READY_WAIT ms — see PHONES below) and only uses the device voice when the
// cloud voice actually fails. The games also warm each question up as soon
// as it appears (prefetchSpeech), so by the time 🔊 is pressed it's ready.
//
// PHONES (Nov 2026, second pass). Three things made the voice late and the
// music loud on a phone:
//   1. Every sentence went through the speakText function, even ones made
//      long ago — a sleeping function takes several seconds to wake. Saved
//      sentences are now looked up straight in ttsCache/<hash> (the same
//      hash the server uses), which answers in a fraction of a second. The
//      function is only called for a sentence nobody has heard yet.
//   2. A brand-new sentence was waited on for up to 25 s. Now a sentence
//      that isn't ready within READY_WAIT is read by the device voice
//      straight away, and the natural one keeps being made in the
//      background so it's ready next time. Games warm their whole question
//      set when they open (prefetchSpeech), so this is rare.
//   3. iPhones and iPads ignore audio.volume, so "turn the music down while
//      reading" did nothing there. On those devices any looping music is
//      muted while the voice reads and comes back after. The voice itself
//      plays through ONE audio player that is unlocked by the child's first
//      tap, because iOS refuses to start a brand-new player from code.
//
// HOME PAGE + "MUSIC GOES QUIET, NOTHING SPEAKS" (Nov 2026, third pass).
//   1. Visitors to the public home page aren't signed in, so every sentence
//      went straight to the device voice — which on many computers never
//      starts (or never says it finished). The game had already turned its
//      music down and waited for an "end" that never came: silence. Now the
//      saved natural voice is used for visitors too (ttsCache and the voice
//      settings are readable without signing in, and speakText makes new
//      sentences for visitors within a daily allowance).
//   2. The device voice is now watched: if it hasn't started in 2.5 s, or
//      runs far past the length of the sentence, it is stopped and the game
//      is told it finished, so the music always comes back.
//   3. The saved voice settings are waited for (briefly) before working out
//      which saved sentence to play — the first sentences of a visit were
//      looked up under the default voice, missed, and fell back to the
//      robotic voice.
//   4. A brand-new sentence now gets up to READY_WAIT (6 s) to be made in
//      the natural voice before the device voice is used — the device voice
//      is a last resort, not the norm.
//
// SPEED, VOICE AND TONE come from the admin's Read-aloud voice settings
// (Layout Builder → 🔊 Read-aloud voice; rewardSettings/main.voice):
//   voice.name / voice.style   which voice and how it sounds (server side,
//                              functions/voice.js)
//   voice.speed.question       questions and instructions in the quiz games
//   voice.speed.answer         answers and cards in the quiz games
//   voice.speed.other          every other read-aloud in the app
// A game marks what it is reading with utterance.sblKind = "question" |
// "answer"; anything unmarked uses the "other" speed times its own .rate.

import { httpsCallable } from "firebase/functions";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db, functions } from "../firebase/firebaseConfig";
import { chooseBestVoice, getVoices } from "../utils/speech";

const urlCache = new Map(); // text -> Promise<url>
let anonNoNew = false; // older server: visitors can't have new sentences made
let disabled = false; // the function isn't there — stop asking this session
// How long a sentence may take to be ready before the device voice reads it
// instead (the natural one keeps being made, for next time). A game can ask
// for a different wait on one utterance with utterance.sblWait (ms).
const READY_WAIT = 6000;

// The same defaults as functions/voice.js — they make up the cache hash, so
// they must match it exactly.
const SERVER_DEFAULT_VOICE = "Aoede";
const SERVER_DEFAULT_STYLE =
  'Say this warmly and clearly at a lively, natural pace (not slow), like a friendly teacher reading to young children. Say the letter A as "ay"';

/* ---------- admin settings: speeds (voice + tone are applied on the server) ---------- */

export const DEFAULT_SPEEDS = { question: 1.23, answer: 1.38, other: 1.12 };
export const SPEED_MIN = 0.7;
export const SPEED_MAX = 1.8;
let voiceSettings = {};
const settingsListeners = new Set();
let settingsStarted = false;
let settingsLoaded = false;
let markSettingsLoaded = () => {};
const settingsReady = new Promise((resolve) => {
  markSettingsLoaded = resolve;
});

// Listens whether or not anyone is signed in (the home page demo reads it
// too), and starts again when the sign-in changes.
const startSettings = () => {
  if (settingsStarted || typeof window === "undefined") return;
  settingsStarted = true;
  let unsub = null;
  const listen = () => {
    if (unsub) unsub();
    unsub = onSnapshot(
      doc(db, "rewardSettings", "main"),
      (snap) => {
        voiceSettings = (snap.exists() && snap.data()?.voice) || {};
        settingsLoaded = true;
        markSettingsLoaded();
        settingsListeners.forEach((f) => f(voiceSettings));
      },
      () => {
        settingsLoaded = true; // can't read them — use the defaults
        markSettingsLoaded();
      }
    );
  };
  onAuthStateChanged(getAuth(), () => listen());
};

/** the settings, or the defaults if they take more than a moment */
const whenSettings = () =>
  settingsLoaded ? Promise.resolve() : Promise.race([settingsReady, new Promise((r) => setTimeout(r, 1500))]);

const clampSpeed = (v, d) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.max(SPEED_MIN, Math.min(SPEED_MAX, n)) : d;
};

/** Playback speed for "question" | "answer" | "other", after admin settings. */
export const speedFor = (kind) => {
  const k = DEFAULT_SPEEDS[kind] ? kind : "other";
  return clampSpeed(voiceSettings?.speed?.[k], DEFAULT_SPEEDS[k]);
};

/** The saved voice settings ({ name, style, speed }); fn is told of changes. */
export const getVoiceSettings = () => voiceSettings;
export const onVoiceSettings = (fn) => {
  settingsListeners.add(fn);
  startSettings();
  return () => settingsListeners.delete(fn);
};

const clean = (t) =>
  String(t || "")
    .replace(/\s+/g, " ")
    .trim()
    // an answer LETTER "A" ("A. Earth", "Choice A:", "(A)") was read as "uh":
    // spell it the way it sounds. "A cat" (the word) is left alone.
    .replace(/(^|[\s(])A(?=[.):,]|$)/g, "$1Ay");

/* ---------- saved sentences, read straight from the cache ---------- */

const sha1 = async (str) => {
  const buf = await window.crypto.subtle.digest("SHA-1", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/** The saved .wav for this sentence in the saved voice, or null. */
const savedUrl = async (key) => {
  try {
    if (!window.crypto?.subtle) return null;
    startSettings();
    await whenSettings();
    const name = String(voiceSettings?.name || SERVER_DEFAULT_VOICE).replace(/[^A-Za-z]/g, "") || SERVER_DEFAULT_VOICE;
    const style = String(voiceSettings?.style || SERVER_DEFAULT_STYLE).slice(0, 200);
    const text = key.slice(0, 600); // exactly what functions/voice.js hashes
    const hash = await sha1(`${name}|${style}|${text}`);
    const snap = await getDoc(doc(db, "ttsCache", hash));
    return (snap.exists() && snap.data()?.url) || null;
  } catch {
    return null; // rules not deployed yet, offline… — ask the function
  }
};

const fetchUrl = (text) => {
  const key = clean(text);
  if (!urlCache.has(key)) {
    const p = savedUrl(key)
      .then((u) => {
        if (u) return u;
        if (anonNoNew && !getAuth().currentUser) return null;
        return httpsCallable(functions, "speakText")({ text: key }).then((r) => r?.data?.url || null);
      })
      .catch((err) => {
        const code = String(err?.code || "");
        // not deployed: stop asking this visit. (A visitor who isn't signed in
        // and gets "unauthenticated" is talking to an older speakText — the
        // saved sentences still work for them, so only new ones stop.)
        if (code.includes("not-found")) disabled = true;
        if (code.includes("unauthenticated")) anonNoNew = true;
        urlCache.delete(key);
        return null;
      });
    urlCache.set(key, p);
  }
  return urlCache.get(key);
};

/** Warm the cache for sentences about to be read (e.g. the next question). */
export const prefetchSpeech = (texts = []) => {
  if (disabled) return;
  texts.filter((t) => t && /[a-z0-9]/i.test(String(t))).forEach((t) => fetchUrl(t));
};

/**
 * Admin "prepare every game's voice": makes (or finds) one sentence and
 * resolves to its url, or null. Same cache and same text clean-up as play.
 */
export const prepareSpeech = (text) => fetchUrl(text);
export const speechCacheKey = (text) => clean(text);

const fire = (utter, type, extra = {}) => {
  let ev;
  try {
    ev = new window.SpeechSynthesisEvent(type, { utterance: utter, charIndex: 0, elapsedTime: 0, name: "", ...extra });
  } catch {
    ev = new Event(type);
    Object.entries(extra).forEach(([k, v]) => {
      try {
        Object.defineProperty(ev, k, { value: v });
      } catch {
        /* ignore */
      }
    });
  }
  try {
    utter.dispatchEvent(ev);
  } catch {
    /* ignore */
  }
};

export const installCloudVoice = () => {
  if (typeof window === "undefined" || !window.speechSynthesis || window.__sblCloudVoice) return;
  startSettings();
  const synth = window.speechSynthesis;
  const native = { speak: synth.speak.bind(synth), cancel: synth.cancel.bind(synth) };
  window.__sblCloudVoice = true;

  const queue = []; // utterances waiting
  let current = null; // { utter, audio, timer, done }
  let nativeBusy = false;

  const speaking = () => Boolean(current) || queue.length > 0 || nativeBusy;

  /* ---------- one voice player, unlocked by the first tap (iOS) ---------- */
  const ua = navigator.userAgent || "";
  const isIOS = /iP(hone|ad|od)/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const volumeLocked = (() => {
    if (isIOS) return true;
    try {
      const t = new Audio();
      t.volume = 0.5;
      return Math.abs(t.volume - 0.5) > 0.01;
    } catch {
      return false;
    }
  })();
  const player = new Audio();
  player.preload = "auto";
  player.setAttribute("playsinline", "");
  let unlocked = false;
  const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    ["pointerdown", "touchend", "keydown", "click"].forEach((t) => document.removeEventListener(t, unlock, true));
    if (!current) {
      try {
        player.src = SILENT;
        const p = player.play();
        if (p && p.catch) p.catch(() => {});
      } catch {
        /* ignore */
      }
    }
    // iOS also wants the device voice started once from a tap
    try {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      native.speak(u);
    } catch {
      /* ignore */
    }
  };
  ["pointerdown", "touchend", "keydown", "click"].forEach((t) => document.addEventListener(t, unlock, { capture: true, passive: true }));

  /* ---------- music down while the voice reads (devices that ignore .volume) ---------- */
  // Games turn their music down with audio.volume, which iPhones ignore. So
  // there, every looping sound that is playing is muted while the voice
  // reads, and only the ones muted here are un-muted afterwards.
  const loops = new Set();
  if (volumeLocked && window.HTMLMediaElement) {
    const origPlay = window.HTMLMediaElement.prototype.play;
    window.HTMLMediaElement.prototype.play = function playAndTrack(...args) {
      if (this !== player && this.loop) loops.add(this);
      return origPlay.apply(this, args);
    };
  }
  let ducked = [];
  let unduckTimer = null;
  const duck = () => {
    if (!volumeLocked) return;
    clearTimeout(unduckTimer);
    loops.forEach((el) => {
      if (!el.isConnected && el.paused) {
        loops.delete(el);
        return;
      }
      if (!el.paused && !el.muted) {
        el.muted = true;
        ducked.push(el);
      }
    });
  };
  const unduckSoon = () => {
    if (!volumeLocked || !ducked.length) return;
    clearTimeout(unduckTimer);
    // a short gap so a game reading several sentences in a row doesn't
    // flick the music on between them
    unduckTimer = setTimeout(() => {
      if (speaking()) return;
      ducked.forEach((el) => {
        el.muted = false;
      });
      ducked = [];
    }, 450);
  };
  try {
    Object.defineProperty(synth, "speaking", { configurable: true, get: speaking });
    Object.defineProperty(synth, "pending", { configurable: true, get: () => queue.length > 0 });
  } catch {
    /* some browsers won't allow it — harmless */
  }

  const finish = (type, extra) => {
    const c = current;
    if (!c || c.done) return;
    c.done = true;
    clearInterval(c.timer);
    if (c.audio) {
      c.audio.onended = null;
      c.audio.onerror = null;
      try {
        c.audio.pause();
      } catch {
        /* ignore */
      }
    }
    current = null;
    fire(c.utter, type, extra);
    // eslint-disable-next-line no-use-before-define
    setTimeout(next, 0);
    unduckSoon();
  };

  const viaDevice = (utter) => {
    // the browser voice, for this one sentence — the most natural one the
    // device has, at the admin's speed (device voices run ~12% quicker at 1)
    try {
      if (!utter.voice) {
        const v = chooseBestVoice(getVoices());
        if (v) utter.voice = v;
      }
      if (utter.sblKind) utter.rate = Math.max(0.5, Math.min(2, speedFor(utter.sblKind) / 1.12));
      else utter.rate = Math.max(0.5, Math.min(2, (speedFor("other") / 1.12) * (Number(utter.rate) || 1)));
    } catch {
      /* ignore */
    }
    nativeBusy = true;
    duck();
    let finished = false;
    let started = false;
    let startTimer = null;
    let lengthTimer = null;
    const done = () => {
      if (finished) return;
      finished = true;
      clearTimeout(startTimer);
      clearTimeout(lengthTimer);
      nativeBusy = false;
      setTimeout(next, 0); // eslint-disable-line no-use-before-define
      unduckSoon();
    };
    // Some computers' device voice never starts, or never says it finished.
    // The game has turned its music down and is waiting for "end" — so if
    // the device voice goes quiet, stop it and send "end" ourselves.
    const giveUp = () => {
      if (finished) return;
      try {
        native.cancel();
      } catch {
        /* ignore */
      }
      fire(utter, "end", { charIndex: String(utter.text || "").length, elapsedTime: 0 });
      done();
    };
    const words = String(utter.text || "").split(/\s+/).filter(Boolean).length;
    utter.addEventListener("start", () => {
      started = true;
      clearTimeout(startTimer);
    }, { once: true });
    utter.addEventListener("end", done, { once: true });
    utter.addEventListener("error", done, { once: true });
    startTimer = setTimeout(() => {
      if (!started) giveUp();
    }, 2500);
    lengthTimer = setTimeout(giveUp, 4000 + (words * 650) / Math.max(0.5, Number(utter.rate) || 1));
    window.__sblLastUtterance = utter; // Chrome drops "end" if nothing holds the utterance
    try {
      if (synth.paused) synth.resume();
    } catch {
      /* ignore */
    }
    native.speak(utter);
  };

  const next = async () => {
    if (current || nativeBusy || !queue.length) return;
    const utter = queue.shift();
    const text = clean(utter.text);
    if (!text) {
      fire(utter, "end");
      setTimeout(next, 0);
      return;
    }
    if (disabled) {
      viaDevice(utter);
      return;
    }
    const me = { utter, audio: null, timer: null, done: false };
    current = me;
    const wait = Number(utter.sblWait) > 0 ? Number(utter.sblWait) : READY_WAIT;
    const url = await Promise.race([fetchUrl(text), new Promise((r) => setTimeout(() => r("slow"), wait))]);
    if (me.done) return; // cancelled while we waited
    if (!url || url === "slow") {
      // not ready yet: the device voice reads it now; the natural one is
      // still being made and is used the next time this sentence comes up
      current = null;
      viaDevice(utter);
      return;
    }
    const audio = player;
    me.audio = audio;
    // speed from the admin's settings: a quiz game's question / answer speed
    // as set, anything else the "other" speed times the game's own .rate
    // (pitch stays natural)
    const rate = Math.max(0.6, Math.min(2, utter.sblKind ? speedFor(utter.sblKind) : speedFor("other") * (Number(utter.rate) || 1)));
    audio.onended = null;
    audio.onerror = null;
    audio.onloadedmetadata = null;
    audio.src = url;
    audio.volume = Math.max(0, Math.min(1, utter.volume ?? 1));
    try {
      audio.preservesPitch = true;
      audio.webkitPreservesPitch = true;
    } catch {
      /* older browsers */
    }
    // Safari resets the speed when a new file loads — set it again then
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
    audio.onloadedmetadata = () => {
      audio.playbackRate = rate;
    };
    audio.onended = () => finish("end", { charIndex: text.length, elapsedTime: audio.currentTime * 1000 });
    audio.onerror = () => {
      if (me.done) return;
      current = null;
      viaDevice(utter);
    };
    // word-by-word progress, for games that highlight as they read
    const words = [];
    text.replace(/\S+/g, (w, i) => words.push(i));
    let sent = 0;
    me.timer = setInterval(() => {
      if (!audio.duration || !Number.isFinite(audio.duration)) return;
      const upTo = Math.floor((audio.currentTime / audio.duration) * words.length);
      while (sent < upTo && sent < words.length) {
        fire(utter, "boundary", { name: "word", charIndex: words[sent], elapsedTime: audio.currentTime * 1000 });
        sent += 1;
      }
    }, 80);
    try {
      duck();
      await audio.play();
      unlocked = true;
      if (!me.done) fire(utter, "start");
    } catch {
      // autoplay blocked or bad file — device voice instead
      if (me.done) return;
      clearInterval(me.timer);
      current = null;
      viaDevice(utter);
    }
  };

  synth.speak = (utter) => {
    if (!utter) return;
    queue.push(utter);
    next();
  };

  synth.cancel = () => {
    const waiting = queue.splice(0, queue.length);
    if (current) finish("error", { error: "interrupted" });
    waiting.forEach((u) => fire(u, "error", { error: "canceled" }));
    nativeBusy = false;
    native.cancel();
    unduckSoon();
  };

  // games often stop speech when they unmount; also stop if the tab is hidden
  document.addEventListener("visibilitychange", () => {
    if (document.hidden && speaking()) synth.cancel();
  });
};

export default installCloudVoice;
